import { useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import classNames from 'classnames/bind';
import styles from './TableClassicReport.module.scss';
import { ColumnType } from '~/utils/types/common';
import { ArrowV3Icon } from '~/assets/icons';

const cx = classNames.bind(styles);

export interface TableClassicReportProps<T> {
    title?: string;
    columns: ColumnType[];
    data: T[];
    isLoading?: boolean;
    error?: string | null;
    emptyMessage?: string;
    // Pagination props
    page: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
}

const TableClassicReport = <T extends { id?: string | number }>({
    title,
    columns,
    data,
    isLoading = false,
    error = null,
    emptyMessage = 'No data found.',
    page,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
}: TableClassicReportProps<T>) => {
    const toItem = totalItems === 0 ? 0 : (page - 1) * itemsPerPage + data.length;
    const footerText = totalItems === 0
        ? 'No items to display'
        : `Showing ${Math.min(toItem, totalItems)} of ${totalItems} items`;

    const pageNumbers = useMemo(() => {
        return totalPages > 0 ? Array.from({ length: totalPages }, (_, idx) => idx + 1) : [];
    }, [totalPages]);

    const canGoPrev = page > 1;
    const canGoNext = totalPages > 0 && page < totalPages;

    const handlePageClick = (targetPage: number) => {
        if (targetPage !== page && targetPage >= 1 && targetPage <= totalPages) {
            onPageChange(targetPage);
        }
    };

    const handlePrev = () => {
        if (canGoPrev) {
            onPageChange(page - 1);
        }
    };

    const handleNext = () => {
        if (canGoNext) {
            onPageChange(page + 1);
        }
    };

    const getRowKey = (row: T, index: number): string | number => {
        return row.id ?? index;
    };

    const headerScrollRef = useRef<HTMLDivElement>(null);
    const bodyScrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const headerElement = headerScrollRef.current;
        const bodyElement = bodyScrollRef.current;

        if (!headerElement || !bodyElement) return;

        const handleBodyScroll = () => {
            if (headerElement) {
                headerElement.scrollLeft = bodyElement.scrollLeft;
            }
        };

        const handleHeaderScroll = () => {
            if (bodyElement) {
                bodyElement.scrollLeft = headerElement.scrollLeft;
            }
        };

        bodyElement.addEventListener('scroll', handleBodyScroll);
        headerElement.addEventListener('scroll', handleHeaderScroll);

        return () => {
            bodyElement.removeEventListener('scroll', handleBodyScroll);
            headerElement.removeEventListener('scroll', handleHeaderScroll);
        };
    }, []);

    return (
        <Box className={cx('table-box')}>
            {title && (
                <div className={cx('table-header-bar')}>
                    <Typography className={cx('report-title')}>{title}</Typography>
                </div>
            )}
            <div className={cx('table-wrapper')}>
                <div ref={headerScrollRef} className={cx('table-header-wrapper')}>
                    <TableContainer component={Paper} elevation={0} className={cx('table-header-container')}>
                        <Table className={cx('classic-table')}>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column: ColumnType) => (
                                        <TableCell
                                            key={column.id}
                                            align="left"
                                            style={{
                                                width: column.width,
                                                minWidth: column.minWidth,
                                            }}
                                        >
                                            {column.labelHeader}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                        </Table>
                    </TableContainer>
                </div>
                <div ref={bodyScrollRef} className={cx('table-body-wrapper')}>
                    <TableContainer component={Paper} elevation={0} className={cx('table-body-container')}>
                        <Table className={cx('classic-table')}>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center" className={cx('empty-cell')}>
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : error ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center" className={cx('empty-cell')}>
                                            {error}
                                        </TableCell>
                                    </TableRow>
                                ) : data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} align="center" className={cx('empty-cell')}>
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    data.map((row, index) => (
                                        <TableRow key={getRowKey(row, index)} className={cx('table-row')}>
                                            {columns.map((column) => (
                                                <TableCell
                                                    key={column.id}
                                                    align="left"
                                                    style={{
                                                        width: column.width,
                                                        minWidth: column.minWidth,
                                                    }}
                                                >
                                                    {column.renderCell(row, index)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
            <Box className={cx('footer-bar')}>
                <Typography className={cx('footer-text')}>{footerText}</Typography>
                {totalPages > 0 && (
                    <Box className={cx('pagination')}>
                        <button
                            type="button"
                            className={cx('page-button', { secondary: true })}
                            onClick={handlePrev}
                            disabled={!canGoPrev}
                        >
                            <ArrowV3Icon className={cx('arrow-icon', 'rotate-180')} />
                        </button>
                        {pageNumbers.map((pageNumber) => (
                            <button
                                type="button"
                                key={pageNumber}
                                className={cx('page-button', {
                                    active: pageNumber === page,
                                    secondary: pageNumber !== page,
                                })}
                                onClick={() => handlePageClick(pageNumber)}
                            >
                                {pageNumber}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={cx('page-button', { secondary: true })}
                            onClick={handleNext}
                            disabled={!canGoNext}
                        >
                            <ArrowV3Icon className={cx('arrow-icon')} />
                        </button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default TableClassicReport;
